/**
 * PAYMENTS — `getPaymentsView`, `listStatements`, `getStatement`, `getPayoutMethod` (through the
 * Payment details card), and `listEarlierMonths` for the months before the payouts here.
 *
 * THE PROTOTYPE'S LOOK, IN PLAIN WORDS (design 15, the owner's live look at real cabinets,
 * 2026-10-01). The screen is the prototype's Payments (`moonx-authors-prototype/MoonX App.dc.html`,
 * `cabIsPayments`): a row of money cards, "This month in detail" as a two-column card with its
 * total in gold, the payout rules, the statements as a list card with a pill per month, and the
 * monthly payouts. Only what the payments view serves is drawn - none of the prototype's own
 * figures or promises ("Reports land on the 10th", per-article rates, "Request payout"). And the
 * author is a user: the screen never says "ledger" (it is their balance), "run" or "UTC".
 *
 * A READ-TIME SUM OVER THE LEDGER, NEVER A STORED BALANCE. `availableMinor` is "the sum of the
 * author's ledger at this instant"; no balance column exists anywhere in the design, and
 * `computedAt` is on the response precisely because "two reads a second apart may legitimately
 * differ". The screen says when it was worked out.
 *
 * THE AVAILABLE FIGURE MAY BE NEGATIVE AND IS NEVER CLAMPED. A refunded tip is a real deduction.
 * `negativeReason` is printed beside it when the platform gives one, because a number below zero
 * with no explanation is the single most alarming thing this screen can show.
 *
 * THERE IS NO "LIFETIME GROSS EARNED" FIGURE ON THIS SURFACE, and this screen does not invent one.
 * `gross` and `all-time` occur ZERO times in authors-api.yaml; what exists is the ledger sum right
 * now plus a `breakdown` of it by transaction kind. The third card is "Paid in total" (the owner,
 * 2026-10-05): the earlier months found paid plus the payouts here with a `paidAt`, each exact or
 * not drawn (./paid-in-total.ts) - and otherwise "Paid to date", the payouts here alone, which says
 * earlier payments are not in it (./paid-to-date.ts argues each condition).
 *
 * THE BREAKDOWN ADDS UP TO THE FIGURE, EXACTLY, AND THE SCREEN SAYS SO (design 14, F14). The live
 * walk of 2026-10-01 drew "$0.00 on your ledger" above parts adding to $55.33: the breakdown left
 * out the three ledger kinds that move an author's balance and belong to no earning member -
 * `opening_balance`, `payout_approval`, `payout_failure_reversal`. The contract now carries them as
 * `openingBalanceMinor` ("From earlier") and `payoutsMinor` ("Paid out or on its way", zero or
 * negative), every member is a line here, zeros included as the others are, and the card's total
 * row is the figure itself. A payout is not drawn in the danger colour: money that left for the
 * author is not a deduction from them. "This month in detail" adds up the same way: the balance
 * now plus the articles' month is the server's `projectedTotalMinor` (it is defined as exactly
 * that sum, and this screen adds nothing itself).
 *
 * THE THRESHOLD SENTENCE NAMES WHAT IT COMPARES. `threshold.isMet` is "whether the available
 * figure clears it" - the ledger right now, AFTER anything paid out or on its way. So when a payout
 * has left the ledger the sentence says that money is not counted, rather than telling an author
 * whose $55.33 is on its way that "the balance carries over".
 *
 * THE PAYOUTS TABLE NEVER SAYS "SENT" FOR MONEY THAT WAS NOT SENT (design 14, F14). The amount
 * column was headed "Sent" and printed `amountMinor`, which is set when the run is WORKED OUT - not
 * when money moves (`payout_run_item.amount_minor`, inserted at compute; `paid_at` is written by
 * the `paid` move alone). It is now headed "Payout" and every amount carries its own word: "Sent"
 * only with `paidAt`; "Not sent" once the item failed or was rejected (a `payout_failure_reversal`
 * put the money back) or was cancelled; "Would send" while `status` is null, because an approved
 * run puts every payable item at `pending` (`PAYOUT_ITEM_STATUS_ON_APPROVAL`) and a null status on
 * a payable item is therefore a run that was worked out and not approved; "To send" otherwise.
 * "Rolled over" is printed only when the run sent nothing (not payable, or no amount): on a
 * payable run `closingBalanceMinor` is opening + earnings + award BEFORE the payout
 * (`payout_run_item_check`), the very money the payout takes, so "carried over" beside it was the
 * same $55.33 counted twice. The status word `sent` is "With the payment provider", not "Sent", for
 * the same reason - it has no `paidAt` (./payout-status.tsx holds the words).
 *
 * AN EDITOR'S CHOICE AWARD SHOWS AS THE AWARD ALONE. No field here is a fee plus an award, and a
 * first article earns nothing, ever.
 *
 * NO "REQUEST PAYOUT" BUTTON. The prototype draws one twice. Authors never move money on this
 * platform: a run computes, an operator approves, and the transfer happens. There is no operation
 * behind such a button and there is not meant to be (design 15, decision 1).
 *
 * PAYMENT DETAILS ARE THE PAGE'S FIRST CARD, READ HERE AND WRITTEN AT `/payments/method` (design
 * 25; the owner's D1: its own page, with a prominent card at the top of Payments). The card
 * (./PaymentDetailsSummaryCard.tsx) says whether the record is complete, keeps the address masked,
 * and names the day complete details are needed by, with this view's `computedAt` as "today".
 * `updatePayoutMethod` is the ONE re-auth-gated operation on the whole API — 403 REAUTH_REQUIRED
 * when the token's authentication is older than the window — so it has a screen of its own that
 * answers that (./PayoutMethodRoute.tsx), and the card links there, in place.
 *
 * THE EARLIER MONTHS ARE HISTORY TO LOOK AT, NEVER MONEY (design 17, the owner, 2026-10-05). After
 * the monthly payouts, "Earlier months" shows each month from before the payouts here as its
 * monthly report stated it - from the author's articles, tips, carried in from the month before,
 * the month's total - and the month's articles behind a button. None of it is in the balance: what
 * was still due when these figures began is "From earlier" above, so no balance adds the months up;
 * only "Paid in total" counts the months found paid, as money already sent. "Paid" is drawn only for
 * a month found paid, "Rolled over to the next month" only for one found carried, and no pill where
 * nobody could tell; a figure the report did not state readably is "Not recorded", never a zero. An
 * author with no earlier month gets no section, and a read that fails is the same quiet partial
 * note the payout method gets, the rest of the page unaffected. The Statements' empty words point
 * down to it when there are earlier months, and otherwise say earlier payments are not listed. The
 * parts are at the foot of this file.
 */
import { Alert, Button, Group, Stack, Table, Text, VisuallyHidden } from '@mantine/core';
import { useCallback, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { NotFoundFailure, isApiProblem, useCabinetApi } from '../../features/cabinet/api';
import {
  CabinetPageHeader,
  EmptyState,
  ErrorState,
  LoadMore,
  LoadingState,
  Money,
  PartialFailureNote,
  RefreshingNote,
  SectionLabel,
  StatTile,
  TileRow,
} from '../../features/cabinet/components';
import { useAsync, useKeysetList } from '../../features/cabinet/state';
import type { AsyncResult } from '../../features/cabinet/state';
import {
  formatCount,
  formatDay,
  formatInstant,
  formatMoney,
  formatPeriod,
} from '../../features/cabinet/format';
import { paidInTotal } from './paid-in-total';
import type { PaidInTotal } from './paid-in-total';
import { paidToDateMinor } from './paid-to-date';
import { PayoutPill } from './payout-status';
import { PaymentDetailsSummaryCard } from './PaymentDetailsSummaryCard';
import { PAYOUT_METHOD_PATH } from './PayoutMethodRoute';
import { StatementPanel } from './StatementPanel';
import classes from './payments.module.css';
import type {
  OperationResult,
  PaymentsView,
  PayoutEntry,
  StatementSummary,
} from '../../features/cabinet/api';
import type { PayoutBlockReason } from '../../features/cabinet/api/cabinet-api';
import { Trans, useTranslation } from '../../i18n';
import type { TFunction } from 'i18next';

/** An item that ended without being paid: whatever its run worked out, it was not sent. */
const NOT_SENT_STATUSES: ReadonlySet<string> = new Set(['failed', 'rejected', 'cancelled']);

/** The two moves that write a `payout_failure_reversal`, putting the amount back on the ledger. */
const PUT_BACK_STATUSES: ReadonlySet<string> = new Set(['failed', 'rejected']);

/** True when the run sent nothing: not payable, or no amount. The only case "rolled over" fits. */
function sentNothing(payout: PayoutEntry): boolean {
  return (
    !payout.payable ||
    payout.amountMinor === undefined ||
    payout.amountMinor === null ||
    payout.amountMinor === 0
  );
}

/**
 * The word in front of a run's amount. "Sent" ONLY with `paidAt`; a payable item with a null status
 * is a run that was worked out and not approved (approval moves every payable item to `pending`).
 */
function payoutAmountWord(payout: PayoutEntry, t: TFunction<'payments'>): string {
  if (payout.paidAt !== undefined && payout.paidAt !== null) {
    return t('payouts.amountWord.sent');
  }
  if (payout.status === undefined || payout.status === null) {
    return t('payouts.amountWord.wouldSend');
  }
  return NOT_SENT_STATUSES.has(payout.status)
    ? t('payouts.amountWord.notSent')
    : t('payouts.amountWord.toSend');
}

/** The reasons the catalogue words (`payouts.blockReason.<reason>`); any other is shown as itself. */
const BLOCK_REASONS: ReadonlySet<string> = new Set<PayoutBlockReason>([
  'author_disabled',
  'no_payout_method',
  'payment_details_incomplete',
]);

function blockReasonWords(reason: string, t: TFunction<'payments'>): string {
  return BLOCK_REASONS.has(reason)
    ? t(`payouts.blockReason.${reason as PayoutBlockReason}`)
    : reason;
}

/** The sections' headings, which name their regions. One of each on the page. */
const HEADING = {
  thisMonth: 'payments-this-month',
  balance: 'payments-balance',
  minimum: 'payments-minimum',
  statements: 'payments-statements',
  payouts: 'payments-payouts',
  earlier: 'payments-earlier',
} as const;

export interface PaymentsRouteProps {
  /** Where Payment details live - ./PayoutMethodRoute.tsx, which answers the re-auth. */
  payoutMethodHref?: string;
}

export function PaymentsRoute({ payoutMethodHref = PAYOUT_METHOD_PATH }: PaymentsRouteProps) {
  const { t } = useTranslation('payments');
  const api = useCabinetApi();
  const [openPeriod, setOpenPeriod] = useState<string | undefined>(undefined);

  const readPayments = useCallback((signal: AbortSignal) => api.getPaymentsView(signal), [api]);
  const readStatements = useCallback(
    (cursor: string | undefined, signal: AbortSignal) =>
      api.listStatements(cursor === undefined ? {} : { cursor }, signal),
    [api],
  );

  const readEarlier = useCallback((signal: AbortSignal) => api.listEarlierMonths(signal), [api]);

  const payments = useAsync(readPayments, 'payments:view');
  const statements = useKeysetList<StatementSummary>(readStatements, 'payments:statements');
  const earlier = useAsync(readEarlier, 'payments:earlier-months');
  const hasEarlierMonths = (earlier.data?.months.length ?? 0) > 0;

  return (
    <Stack gap="xl">
      <CabinetPageHeader title={t('route.title')} lede={t('route.lede')} />

      {/* ---- payment details first (design 25, D1): read here, changed on their own page --- */}
      <PaymentDetailsSummaryCard
        href={payoutMethodHref}
        today={payments.data?.computedAt ?? null}
      />

      {/* ---- the money --------------------------------------------------------------------- */}
      {payments.status === 'loading' && payments.data === undefined ? (
        <LoadingState label={t('route.working')} rows={3} />
      ) : payments.status === 'error' && payments.data === undefined ? (
        <ErrorState
          title={t('route.figuresFailed')}
          error={payments.error}
          onRetry={payments.refetch}
        />
      ) : payments.data === undefined ? null : (
        <>
          <EstimatesBetaNote />
          <MoneyCards
            payments={payments.data}
            earlier={earlier}
            isRefreshing={payments.isRefreshing}
          />
          <ThisMonthInDetail payments={payments.data} />
          <BalanceInDetail payments={payments.data} />
          <MinimumPayout payments={payments.data} />
        </>
      )}

      {/* ---- statements -------------------------------------------------------------------- */}
      <section aria-labelledby={HEADING.statements}>
        <SectionLabel id={HEADING.statements}>{t('statements.heading')}</SectionLabel>

        {statements.isInitialLoad ? (
          <LoadingState label={t('statements.reading')} rows={3} />
        ) : statements.status === 'error' && statements.items.length === 0 ? (
          <ErrorState
            title={t('statements.failed')}
            error={statements.error}
            onRetry={statements.reload}
          />
        ) : statements.items.length === 0 ? (
          <EmptyState
            headline={t('statements.empty.headline')}
            explanation={
              // The months before the first payout here are not missing when there are earlier
              // months: they are further down the page.
              hasEarlierMonths
                ? t('statements.empty.withEarlierMonths')
                : t('statements.empty.withoutEarlierMonths')
            }
          />
        ) : (
          <Stack gap="sm">
            {statements.isLoadingMore ? <RefreshingNote label={t('route.loadingMore')} /> : null}
            <ul className={classes.list} aria-label={t('statements.listLabel')}>
              {statements.items.map((row) => (
                <StatementRow
                  key={row.period}
                  row={row}
                  isOpen={openPeriod === row.period}
                  onToggle={() => {
                    setOpenPeriod(openPeriod === row.period ? undefined : row.period);
                  }}
                  onClose={() => {
                    setOpenPeriod(undefined);
                  }}
                />
              ))}
            </ul>
            <LoadMore
              hasMore={statements.hasMore}
              isLoading={statements.isLoadingMore}
              noun={t('statements.noun')}
              count={statements.items.length}
              onLoadMore={statements.loadMore}
            />
          </Stack>
        )}
      </section>

      {/* ---- the monthly payouts ----------------------------------------------------------- */}
      {payments.data === undefined ? null : <PayoutHistory payments={payments.data} />}

      {/* ---- the earlier months: for the author's records, never in the balance ------------- */}
      <EarlierMonths earlier={earlier} />
    </Stack>
  );
}

/* ------------------------------------------------------------------------------ the parts */

/**
 * The estimates on this page count views the new way while the payout is still worked out the old
 * one, so the two can differ (the owner, 2026-10-20). Said once, above the figures it is about, and
 * only when they are on the page - after Payment details, which stays the first card (D1).
 */
function EstimatesBetaNote() {
  const { t } = useTranslation('payments');
  return (
    <Alert variant="light" color="moon" role="note" title={t('route.estimatesBeta.title')}>
      <Text size="sm">{t('route.estimatesBeta.body')}</Text>
    </Alert>
  );
}

type AmountTone = 'plain' | 'signed' | 'gold' | 'total';

/**
 * The ink an amount is drawn in. `signed` colours a figure below zero (on the card); `gold` is the
 * Available card's figure, below zero still `danger`; `total` is the lifted total row - gold when
 * not below zero, the text ink when it is, because `danger` does not clear 4.5:1 on that row in
 * light (./payments.module.css); `plain` never colours (a payout below zero is money sent).
 */
function inkFor(minor: number, tone: AmountTone): 'danger' | 'gold' | undefined {
  if (tone === 'plain') {
    return undefined;
  }
  if (tone === 'total') {
    return minor < 0 ? undefined : 'gold';
  }
  if (minor < 0) {
    return 'danger';
  }
  return tone === 'gold' ? 'gold' : undefined;
}

/** An amount in a detail card or a card's figure. `data-ink` says which ink, for the specs. */
function Amount({
  minor,
  currency,
  tone = 'signed',
}: {
  readonly minor: number;
  readonly currency: string;
  readonly tone?: AmountTone;
}) {
  const ink = inkFor(minor, tone);
  return (
    <span
      className={ink === undefined ? undefined : ink === 'gold' ? classes.gold : classes.negative}
      data-ink={ink}
    >
      {formatMoney(minor, currency)}
    </span>
  );
}

interface DetailLine {
  readonly label: string;
  readonly note?: string | undefined;
  readonly value: ReactNode;
}

/**
 * The prototype's two-column card: a line per part, the total on a lifted row. A real table, so
 * each amount is read with its label; `label` names it.
 */
function DetailTable({
  label,
  lines,
  total,
}: {
  readonly label: string;
  readonly lines: readonly DetailLine[];
  readonly total?: { readonly label: string; readonly value: ReactNode };
}) {
  return (
    <table className={classes.detail} aria-label={label}>
      <tbody>
        {lines.map((line) => (
          <tr key={line.label}>
            <th scope="row" className={classes.detailLabel}>
              <span className={classes.detailName}>{line.label}</span>
              {line.note === undefined ? null : (
                <span className={classes.detailNote}>{line.note}</span>
              )}
            </th>
            <td className={classes.detailValue}>{line.value}</td>
          </tr>
        ))}
      </tbody>
      {total === undefined ? null : (
        <tfoot>
          <tr className={classes.detailTotal}>
            <th scope="row">{total.label}</th>
            <td className={classes.detailValue}>{total.value}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}

/**
 * The third money card. "Paid in total" once the earlier months have answered and both of its sums
 * are exact (./paid-in-total.ts). Nothing while they are on their way, so the card never changes its
 * name under the author's eyes. Where they cannot be counted - a server without the read, a read that
 * failed, a sum that is not exact - "Paid to date", the payouts here alone, as before.
 */
function paidCard(
  payments: PaymentsView,
  earlier: AsyncResult<EarlierMonthsAnswer>,
  t: TFunction<'payments'>,
): { readonly label: string; readonly minor: number; readonly tip: string } | undefined {
  if (earlier.status === 'loading' && earlier.data === undefined) {
    return undefined;
  }
  const months = earlier.status === 'error' ? undefined : earlier.data;
  const total = months === undefined ? undefined : paidInTotal(payments, months);
  if (total !== undefined) {
    return {
      label: t('money.paidInTotal.label'),
      minor: total.totalMinor,
      tip: paidInTotalTip(total, payments.currency, t),
    };
  }
  const paidToDate = paidToDateMinor(payments);
  return paidToDate === undefined
    ? undefined
    : {
        label: t('money.paidToDate.label'),
        minor: paidToDate,
        tip: t('money.paidToDate.tip'),
      };
}

function paidInTotalTip(total: PaidInTotal, currency: string, t: TFunction<'payments'>): string {
  const range = total.beforeRange;
  if (total.beforeMinor === 0 || range === undefined) {
    return t('money.paidInTotal.tipHere');
  }
  const months =
    range.from === range.to
      ? formatPeriod(range.from)
      : t('money.paidInTotal.monthRange', {
          from: formatPeriod(range.from),
          to: formatPeriod(range.to),
        });
  const amount = formatMoney(total.beforeMinor, currency);
  return total.hereMinor === 0
    ? t('money.paidInTotal.tipEarlier', { amount, months })
    : t('money.paidInTotal.tipEarlierAndHere', {
        amount,
        months,
        here: formatMoney(total.hereMinor, currency),
      });
}

/** The prototype's row of money cards: only figures the payments view serves. */
function MoneyCards({
  payments,
  earlier,
  isRefreshing,
}: {
  readonly payments: PaymentsView;
  readonly earlier: AsyncResult<EarlierMonthsAnswer>;
  readonly isRefreshing: boolean;
}) {
  const { t } = useTranslation('payments');
  const negative = payments.availableMinor < 0;
  const month = payments.currentMonth;
  const paid = paidCard(payments, earlier, t);

  return (
    <Stack gap="sm">
      <TileRow columns={paid === undefined ? 2 : 3}>
        <StatTile
          label={t('money.available.label')}
          value={
            <Amount minor={payments.availableMinor} currency={payments.currency} tone="gold" />
          }
          tip={negative ? t('money.available.tipNegative') : t('money.available.tip')}
        />
        {month === undefined ? (
          // A legitimate state, not a failure: `currentMonth` "exists only while there is no run
          // to answer with", so its absence means the month has been worked out.
          <StatTile
            label={t('money.withMonth.label')}
            value={t('money.withMonth.noEstimate')}
            quiet
            tip={t('money.withMonth.noEstimateTip')}
          />
        ) : (
          <StatTile
            label={t('money.withMonth.label')}
            value={<Amount minor={month.projectedTotalMinor} currency={payments.currency} />}
            tip={t('money.withMonth.tip')}
          />
        )}
        {paid === undefined ? null : (
          <StatTile
            label={paid.label}
            value={<Amount minor={paid.minor} currency={payments.currency} tone="plain" />}
            tip={paid.tip}
          />
        )}
      </TileRow>

      <Group gap="sm" wrap="wrap">
        <Text size="xs" c="dimmed">
          {t('money.workedOut', { when: formatInstant(payments.computedAt) })}
        </Text>
        {isRefreshing ? <RefreshingNote label={t('route.refreshing')} /> : null}
      </Group>

      {negative ? (
        <Alert
          variant="light"
          role="status"
          title={t('money.negative.title')}
          styles={{
            root: {
              backgroundColor: 'oklch(var(--mx-warning) / 0.10)',
              border: '1px solid oklch(var(--mx-warning) / 0.45)',
            },
            // The warning INK: light `warning` read 4.34 on this tint in the card, the ink 6.59;
            // dark is `warning` itself (web/authors/test/tinted-inks.spec.tsx).
            title: { color: 'oklch(var(--mx-warning-ink))' },
          }}
        >
          <Text size="sm">
            {t('money.negative.body', {
              reason: payments.negativeReason ?? t('money.negative.reason'),
            })}
          </Text>
        </Alert>
      ) : null}
    </Stack>
  );
}

/**
 * The open month, priced provisionally (decision-log row 138 item 1; the dashboard's
 * MidMonthEarnings draws the same answer). Its two lines ARE the estimate - the server defines
 * `projectedTotalMinor` as `availableMinor + articlesMinor` and nothing else - and this month's
 * tips are already inside the balance, so they are said, never added a second time. Not final, it
 * can go down, and its last counted day is printed rather than implied.
 */
function ThisMonthInDetail({ payments }: { readonly payments: PaymentsView }) {
  const { t } = useTranslation('payments');
  const month = payments.currentMonth;
  if (month === undefined) {
    return null;
  }
  const name = formatPeriod(month.period);
  const balanceNote =
    month.tipsMinor === 0
      ? t('thisMonth.available.note')
      : t('thisMonth.available.noteWithTips', {
          tips: formatMoney(month.tipsMinor, payments.currency),
        });

  return (
    <section aria-labelledby={HEADING.thisMonth}>
      <SectionLabel id={HEADING.thisMonth}>{t('thisMonth.heading')}</SectionLabel>
      <DetailTable
        label={t('thisMonth.soFar', { month: name })}
        lines={[
          {
            label: t('thisMonth.available.label'),
            note: balanceNote,
            value: <Amount minor={payments.availableMinor} currency={payments.currency} />,
          },
          {
            label: t('thisMonth.articles.label'),
            note: t('thisMonth.articles.note'),
            value: <Amount minor={month.articlesMinor} currency={payments.currency} />,
          },
        ]}
        total={{
          label: t('thisMonth.total', { month: name }),
          value: (
            <Amount minor={month.projectedTotalMinor} currency={payments.currency} tone="total" />
          ),
        }}
      />
      <Stack gap={4} mt="sm">
        <Text size="xs" c="dimmed">
          {t('thisMonth.notFinal', { day: formatDay(month.countedThrough) })}
        </Text>
        {month.meetsThresholdProjected === undefined ? null : (
          <Text size="xs" c="dimmed">
            {month.meetsThresholdProjected
              ? t('thisMonth.wouldReachMinimum')
              : t('thisMonth.wouldNotReachMinimum')}
          </Text>
        )}
      </Stack>
    </section>
  );
}

/** Every member of `breakdown`, so the lines sum to `availableMinor` exactly (design 14, F14). */
function BalanceInDetail({ payments }: { readonly payments: PaymentsView }) {
  const { t } = useTranslation('payments');
  const { breakdown, currency } = payments;
  const money = (minor: number, tone: 'signed' | 'plain' = 'signed') => (
    <Amount minor={minor} currency={currency} tone={tone} />
  );
  // Zeros are lines like the others: a member that comes and goes would make an author wonder
  // where it went.
  const lines: DetailLine[] = [
    {
      label: t('balance.fromEarlier.label'),
      note: t('balance.fromEarlier.note'),
      value: money(breakdown.openingBalanceMinor),
    },
    { label: t('balance.articleEarnings'), value: money(breakdown.articleFeesMinor) },
    {
      label: t('balance.tips.label'),
      note: t('balance.tips.note'),
      value: money(breakdown.tipsMinor),
    },
    {
      label: t('balance.bonuses.label'),
      note: t('balance.bonuses.note'),
      value: money(breakdown.bonusesMinor),
    },
    {
      label: t('balance.awards.label'),
      note: t('balance.awards.note'),
      value: money(breakdown.awardsMinor),
    },
    ...(breakdown.correctionsMinor === undefined
      ? []
      : [{ label: t('balance.adjustments'), value: money(breakdown.correctionsMinor) }]),
    {
      label: t('balance.payouts.label'),
      note: t('balance.payouts.note'),
      // Below zero here is money sent to the author, not taken from them: never the danger ink.
      value: money(breakdown.payoutsMinor, 'plain'),
    },
  ];

  return (
    <section aria-labelledby={HEADING.balance}>
      <SectionLabel id={HEADING.balance}>{t('balance.heading')}</SectionLabel>
      <DetailTable
        label={t('balance.tableLabel')}
        lines={lines}
        total={{
          label: t('balance.total'),
          value: <Amount minor={payments.availableMinor} currency={currency} tone="total" />,
        }}
      />
      <Stack gap={4} mt="sm">
        <Text size="xs" c="dimmed">
          {t('balance.addsUp')}
        </Text>
        {payments.firstArticleUnpaid === true ? (
          <Text size="xs" c="dimmed">
            {t('balance.firstArticle')}
          </Text>
        ) : null}
      </Stack>
    </section>
  );
}

/** The payout rules the view backs: the threshold, and what happens under it. */
function MinimumPayout({ payments }: { readonly payments: PaymentsView }) {
  const { t } = useTranslation('payments');
  // Money already paid out or on its way has left the balance, so the sentence says it is not
  // counted rather than that "the balance carries over" (see the header).
  const paidOut = payments.breakdown.payoutsMinor < 0;
  return (
    <section aria-labelledby={HEADING.minimum}>
      <SectionLabel id={HEADING.minimum}>{t('rules.heading')}</SectionLabel>
      <DetailTable
        label={t('rules.tableLabel')}
        lines={[
          {
            label: t('rules.minimum'),
            value: (
              <Amount
                minor={payments.threshold.thresholdMinor}
                currency={payments.currency}
                tone="plain"
              />
            ),
          },
          { label: t('rules.payouts.label'), value: t('rules.payouts.value') },
          { label: t('rules.underMinimum.label'), value: t('rules.underMinimum.value') },
        ]}
      />
      <Text size="sm" c="dimmed" mt="sm">
        {payments.threshold.isMet
          ? paidOut
            ? t('rules.metPaidOut')
            : t('rules.met')
          : paidOut
            ? t('rules.notMetPaidOut')
            : t('rules.notMet')}
      </Text>
    </section>
  );
}

function StatementRow({
  row,
  isOpen,
  onToggle,
  onClose,
}: {
  readonly row: StatementSummary;
  readonly isOpen: boolean;
  readonly onToggle: () => void;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation('payments');
  const month = formatPeriod(row.period);
  return (
    <li className={classes.listItem}>
      <div className={classes.listRow}>
        <div className={classes.listWhat}>
          <div className={classes.listTitle}>{month}</div>
          {row.renderedAt === undefined || row.renderedAt === null ? null : (
            <div className={classes.listSub}>
              {t('statements.written', { when: formatInstant(row.renderedAt) })}
            </div>
          )}
        </div>
        <PayoutPill status={row.status} />
        <span className={classes.listAmount}>
          <Trans
            t={t}
            i18nKey="statements.settled"
            components={{
              hidden: <VisuallyHidden />,
              amount: <Amount minor={row.settledAmountMinor} currency={row.currency} />,
            }}
          />
        </span>
        <Button size="compact-sm" variant="default" aria-expanded={isOpen} onClick={onToggle}>
          <Trans
            t={t}
            i18nKey={isOpen ? 'statements.hide' : 'statements.read'}
            values={{ month }}
            components={{ hidden: <VisuallyHidden /> }}
          />
        </Button>
      </div>
      {isOpen ? (
        <div className={classes.listPanel}>
          <StatementPanel period={row.period} onClose={onClose} />
        </div>
      ) : null}
    </li>
  );
}

function PayoutHistory({ payments }: { readonly payments: PaymentsView }) {
  const { t } = useTranslation('payments');
  const payouts = payments.payouts ?? [];

  return (
    <section aria-labelledby={HEADING.payouts}>
      <SectionLabel id={HEADING.payouts}>{t('payouts.heading')}</SectionLabel>
      {payouts.length === 0 ? (
        <EmptyState
          headline={t('payouts.empty.headline')}
          explanation={t('payouts.empty.explanation')}
        />
      ) : (
        <div
          className={classes.scroller}
          tabIndex={0}
          role="region"
          aria-label={t('payouts.regionLabel')}
        >
          <Table verticalSpacing="sm" horizontalSpacing="md" style={{ minWidth: 760 }}>
            <Table.Caption>{t('payouts.caption')}</Table.Caption>
            <Table.Thead>
              <Table.Tr>
                <Table.Th scope="col">{t('payouts.columns.month')}</Table.Th>
                <Table.Th scope="col" style={{ textAlign: 'right' }}>
                  {t('payouts.columns.articleEarnings')}
                </Table.Th>
                <Table.Th scope="col" style={{ textAlign: 'right' }}>
                  {t('payouts.columns.award')}
                </Table.Th>
                <Table.Th scope="col" style={{ textAlign: 'right' }}>
                  {t('payouts.columns.payout')}
                </Table.Th>
                <Table.Th scope="col">{t('payouts.columns.whatHappened')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {/* NOT KEYED BY `period` ALONE. The contract gives "One entry per run this author
                  appeared in" and no run id (docs/design/07-api/authors-api.yaml:6340-6346), so one
                  month can hold two runs - measured live on 2026-09-23, a failed run and the run
                  after it both for the same month, which React reported as two children with one
                  key. A row's place in this newest-first answer is what tells two runs apart. */}
              {payouts.map((payout, index) => (
                <Table.Tr key={`${payout.period}#${String(index)}`}>
                  <Table.Th scope="row" style={{ fontWeight: 700 }}>
                    {formatPeriod(payout.period)}
                  </Table.Th>
                  <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {payout.earningsMinor === undefined ? (
                      '—'
                    ) : (
                      <Money minor={payout.earningsMinor} currency={payout.currency} size="sm" />
                    )}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {payout.awardMinor === undefined || payout.awardMinor === 0 ? (
                      <Text size="sm" c="dimmed">
                        —
                      </Text>
                    ) : (
                      <Money
                        minor={payout.awardMinor}
                        currency={payout.currency}
                        size="sm"
                        fw={700}
                      />
                    )}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {sentNothing(payout) ||
                    payout.amountMinor === undefined ||
                    payout.amountMinor === null ? (
                      <Text size="sm" c="dimmed">
                        {t('payouts.nothing')}
                      </Text>
                    ) : (
                      <Text size="sm" component="span">
                        <Text size="sm" component="span" c="dimmed">
                          {payoutAmountWord(payout, t)}
                        </Text>{' '}
                        <Money
                          minor={payout.amountMinor}
                          currency={payout.currency}
                          size="sm"
                          fw={700}
                        />
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={4} align="flex-start">
                      {payout.status === undefined || payout.status === null ? (
                        <PayoutPill
                          status={null}
                          words={
                            payout.payable
                              ? t('payouts.workedOutNotApproved')
                              : t('payouts.notPayable')
                          }
                        />
                      ) : (
                        <PayoutPill status={payout.status} />
                      )}
                      {payout.blockReason === undefined || payout.blockReason === null ? null : (
                        <Text size="xs" c="dimmed">
                          {blockReasonWords(payout.blockReason, t)}
                        </Text>
                      )}
                      {/* Only when the run sent nothing: on a payable run the closing balance IS
                          the money the payout takes, so it was never carried anywhere. */}
                      {!sentNothing(payout) ||
                      payout.closingBalanceMinor === undefined ||
                      payout.closingBalanceMinor === 0 ? null : (
                        <Text size="xs" c="dimmed">
                          <Trans
                            t={t}
                            i18nKey="payouts.rolledOver"
                            components={{
                              money: (
                                <Money
                                  minor={payout.closingBalanceMinor}
                                  currency={payout.currency}
                                  size="xs"
                                  emphasiseNegative
                                />
                              ),
                            }}
                          />
                        </Text>
                      )}
                      {payout.status === undefined ||
                      payout.status === null ||
                      !PUT_BACK_STATUSES.has(payout.status) ||
                      payout.amountMinor === undefined ||
                      payout.amountMinor === null ? null : (
                        <Text size="xs" c="dimmed">
                          <Trans
                            t={t}
                            i18nKey="payouts.putBack"
                            components={{
                              money: (
                                <Money
                                  minor={payout.amountMinor}
                                  currency={payout.currency}
                                  size="xs"
                                />
                              ),
                            }}
                          />
                        </Text>
                      )}
                      {payout.paidAt === undefined || payout.paidAt === null ? null : (
                        <Text size="xs" c="dimmed">
                          {t('payouts.paidAt', { when: formatInstant(payout.paidAt) })}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}
      <Text size="xs" c="dimmed" mt="sm">
        {t('payouts.neverHeldBack')}
      </Text>
    </section>
  );
}

/* ---------------------------------------------------------------------- the earlier months */

type EarlierMonthsAnswer = OperationResult<'listEarlierMonths'>;
type EarlierMonth = EarlierMonthsAnswer['months'][number];
type EarlierMonthArticle = EarlierMonth['articles'][number];

/**
 * "EARLIER MONTHS", AFTER THE MONTHLY PAYOUTS: one entry per month, newest first, in the
 * statements' list card. Nothing while the read is on its way and nothing for an author who has
 * none - a heading over an empty list would promise a history that is not there. A read that
 * failed is the payout method's quiet partial note; the money above does not depend on it.
 */
function EarlierMonths({ earlier }: { readonly earlier: AsyncResult<EarlierMonthsAnswer> }) {
  const { t } = useTranslation('payments');
  // A 404: a server from before this read (the contract declares none) - with a problem body or
  // without one. It has no earlier months to give, which is not a failure to report: the section is
  // left out, as for an author with none.
  if (earlier.status === 'error' && isNotServed(earlier.error)) {
    return null;
  }
  if (earlier.status === 'error') {
    return (
      <section aria-labelledby={HEADING.earlier}>
        <SectionLabel id={HEADING.earlier}>{t('earlier.heading')}</SectionLabel>
        <PartialFailureNote what={t('earlier.failed')} error={earlier.error} />
      </section>
    );
  }
  const months = earlier.data?.months ?? [];
  if (months.length === 0) {
    return null;
  }
  return (
    <section aria-labelledby={HEADING.earlier}>
      <SectionLabel id={HEADING.earlier}>{t('earlier.heading')}</SectionLabel>
      {/* These months are records; what was still due is counted once, above. */}
      <Text size="sm" c="dimmed" mb="sm">
        {t('earlier.note')}
      </Text>
      <ul className={classes.list} aria-label={t('earlier.listLabel')}>
        {months.map((month) => (
          <EarlierMonthItem key={month.period} month={month} />
        ))}
      </ul>
    </section>
  );
}

/**
 * What was found about the month, as a pill whose words carry it: "Paid" only for a month found
 * paid, "Rolled over to the next month" only for one found carried, and NO pill where nobody could
 * tell - the screen claims nothing it was not told. Nor over a month of $0.00: an author whose only
 * article that month was their first one sits on the carried sheet with nothing to carry (the track's
 * answer of 2026-10-05), and "Rolled over" would promise money that never existed.
 */
function EarlierMonthPill({
  status,
  totalMinor,
}: {
  readonly status: EarlierMonth['payoutStatus'];
  readonly totalMinor: EarlierMonth['totalMinor'];
}) {
  const { t } = useTranslation('payments');
  if (status === 'paid') {
    return <PayoutPill status="paid" />;
  }
  if (status === 'carried' && totalMinor !== 0) {
    return <PayoutPill status={null} words={t('earlier.rolledOver')} />;
  }
  return null;
}

/** A figure of the month, or the quiet words when its report did not state it readably. */
function MonthFigure({
  minor,
  currency,
  tone = 'signed',
}: {
  readonly minor: number | null;
  readonly currency: string;
  readonly tone?: AmountTone;
}) {
  const { t } = useTranslation('payments');
  if (minor === null) {
    return <span className={classes.notRecorded}>{t('earlier.notRecorded')}</span>;
  }
  return <Amount minor={minor} currency={currency} tone={tone} />;
}

/**
 * One month: its name and pill, its figures as the screen's two-column card with the month's own
 * total on the lifted row, and its articles behind a real button. The table is always in the page
 * and `hidden` while closed, so the button's `aria-controls` names an element that exists.
 */
function EarlierMonthItem({ month }: { readonly month: EarlierMonth }) {
  const { t } = useTranslation('payments');
  const [open, setOpen] = useState(false);
  const articlesId = useId();
  const name = formatPeriod(month.period);
  const lines = month.articles.length;
  const figure = (minor: number | null) => <MonthFigure minor={minor} currency={month.currency} />;

  return (
    <li className={classes.listItem}>
      <div className={classes.listRow}>
        <h3 className={classes.monthHeading}>{name}</h3>
        <EarlierMonthPill status={month.payoutStatus} totalMinor={month.totalMinor} />
      </div>
      <div className={classes.listPanel}>
        <Stack gap="sm">
          <DetailTable
            label={t('earlier.tableLabel', { month: name })}
            lines={[
              { label: t('earlier.articleEarnings'), value: figure(month.articlesMinor) },
              { label: t('earlier.tips'), value: figure(month.tipsMinor) },
              { label: t('earlier.carriedIn'), value: figure(month.carriedInMinor) },
            ]}
            total={{
              label: t('earlier.total'),
              value: (
                <MonthFigure minor={month.totalMinor} currency={month.currency} tone="total" />
              ),
            }}
          />
          {lines === 0 ? null : (
            <>
              <Group>
                <Button
                  size="compact-sm"
                  variant="default"
                  aria-expanded={open}
                  aria-controls={articlesId}
                  onClick={() => {
                    setOpen(!open);
                  }}
                >
                  <Trans
                    t={t}
                    i18nKey={open ? 'earlier.hideArticles' : 'earlier.showArticles'}
                    count={lines}
                    values={{ month: name }}
                    components={{ hidden: <VisuallyHidden /> }}
                  />
                </Button>
              </Group>
              <div id={articlesId} hidden={!open}>
                <EarlierMonthArticles month={month} name={name} />
              </div>
            </>
          )}
        </Stack>
      </div>
    </li>
  );
}

/** A count as the report stated it, or the quiet words. */
function StatedCount({ value }: { readonly value: number | null }) {
  const { t } = useTranslation('payments');
  return value === null ? (
    <Text size="sm" c="dimmed" component="span">
      {t('earlier.notRecorded')}
    </Text>
  ) : (
    <>{formatCount(value)}</>
  );
}

/**
 * What an article line earned that month. An Editor's Choice is the award, said in words beside
 * its amount; a line the report did not count - the author's first article earns nothing - says
 * "Not counted", never a zero that reads like a fault.
 */
function EarnedThatMonth({
  line,
  currency,
}: {
  readonly line: EarlierMonthArticle;
  readonly currency: string;
}) {
  const { t } = useTranslation('payments');
  if (line.editorsChoice) {
    return (
      <Stack gap={2} align="flex-end">
        {line.earnedMinor === null ? null : (
          <Money minor={line.earnedMinor} currency={currency} size="sm" fw={700} />
        )}
        <Text size="xs" c="dimmed">
          {t('earlier.editorsChoice')}
        </Text>
      </Stack>
    );
  }
  if (line.firstArticle || !line.counted) {
    return (
      <Text size="sm" c="dimmed" component="span">
        {t('earlier.notCounted')}
      </Text>
    );
  }
  if (line.earnedMinor === null) {
    return (
      <Text size="sm" c="dimmed" component="span">
        {t('earlier.notRecorded')}
      </Text>
    );
  }
  return <Money minor={line.earnedMinor} currency={currency} size="sm" />;
}

/** The month's article lines, as the monthly payouts' table is drawn: a card that scrolls sideways. */
function EarlierMonthArticles({
  month,
  name,
}: {
  readonly month: EarlierMonth;
  readonly name: string;
}) {
  const { t } = useTranslation('payments');
  const right = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;
  return (
    <div
      className={classes.scroller}
      tabIndex={0}
      role="region"
      aria-label={t('earlier.articlesLabel', { month: name })}
    >
      <Table verticalSpacing="sm" horizontalSpacing="md" style={{ minWidth: 640 }}>
        <Table.Caption>{t('earlier.caption')}</Table.Caption>
        <Table.Thead>
          <Table.Tr>
            <Table.Th scope="col">{t('earlier.columns.article')}</Table.Th>
            <Table.Th scope="col">{t('earlier.columns.published')}</Table.Th>
            <Table.Th scope="col" style={{ textAlign: 'right' }}>
              {t('earlier.columns.views')}
            </Table.Th>
            <Table.Th scope="col" style={{ textAlign: 'right' }}>
              {t('earlier.columns.likes')}
            </Table.Th>
            <Table.Th scope="col" style={{ textAlign: 'right' }}>
              {t('earlier.columns.comments')}
            </Table.Th>
            <Table.Th scope="col" style={{ textAlign: 'right' }}>
              {t('earlier.columns.articleEarnings')}
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {/* No line id is served and an article may be gone, so a line's place in the report's
              own order is what tells two apart. */}
          {month.articles.map((line, index) => (
            <Table.Tr key={`${line.articleId ?? 'gone'}#${String(index)}`}>
              <Table.Th scope="row" style={{ fontWeight: 700 }}>
                {line.title === null ? (
                  // The article is no longer the author's here, so there is no title to show.
                  <Text size="sm" c="dimmed" component="span">
                    {t('earlier.articleGone')}
                  </Text>
                ) : line.title.length === 0 ? (
                  // The author's own article with no title anywhere yet - as My articles says it.
                  t('earlier.untitled')
                ) : (
                  line.title
                )}
              </Table.Th>
              <Table.Td style={{ whiteSpace: 'nowrap' }}>
                {line.publishedOn === null ? (
                  <Text size="sm" c="dimmed" component="span">
                    {t('earlier.notRecorded')}
                  </Text>
                ) : (
                  formatDay(line.publishedOn)
                )}
              </Table.Td>
              <Table.Td style={right}>
                <StatedCount value={line.views} />
              </Table.Td>
              <Table.Td style={right}>
                <StatedCount value={line.likes} />
              </Table.Td>
              <Table.Td style={right}>
                <StatedCount value={line.comments} />
              </Table.Td>
              <Table.Td style={right}>
                <EarnedThatMonth line={line} currency={month.currency} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  );
}

/** A 404 from a server that does not serve the read yet: with a problem body (`ApiProblem`) or not. */
function isNotServed(error: unknown): boolean {
  return error instanceof NotFoundFailure || (isApiProblem(error) && error.status === 404);
}
